/**
 * authBootstrap
 * Houses bootstrap probing logic separate from React component exports to keep
 * AuthContext hot-reload friendly.
 */
import type { User } from './types';

import { fetchAuthMeAuthedResponse } from '../api/auth';

let _bootstrapAttempted = false;
let _bootstrapInflight: Promise<User | null> | null = null;
let _bootstrapGeneration = 0;
const BOOTSTRAP_RATE_LIMIT_ERROR = 'auth_bootstrap_rate_limited';
const MAX_RATE_LIMIT_RETRIES = 2;
const MIN_RATE_LIMIT_DELAY_MS = 750;
const MAX_RATE_LIMIT_DELAY_MS = 3000;
const INVALID_PAYLOAD_ERROR = 'auth_bootstrap_invalid_payload';
const UNEXPECTED_STATUS_ERROR = 'auth_bootstrap_unexpected_status';

const PUBLIC_BOOTSTRAP_SKIP_PATHS = new Set<string>([
  '/',
  '/about',
  '/contact',
  '/course',
  '/programs',
  '/placement-test',
  '/privacy',
  '/termsofservice',
  '/job-applications',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/auth/google-followup',
  '/placement',
  '/health-dashboard',
  '/diagnostics/dashboard',
  '/summer-training',
]);

const PUBLIC_BOOTSTRAP_SKIP_PREFIXES = [
  '/course/',
  '/placement/',
  '/error/',
  '/summer-training/',
];

function shouldSkipBootstrapForPath(path: string): boolean {
  if (PUBLIC_BOOTSTRAP_SKIP_PATHS.has(path)) return true;
  return PUBLIC_BOOTSTRAP_SKIP_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Reset bootstrap state - call on logout to allow fresh auth check on next page load. */
export function resetBootstrap(): void {
  _bootstrapGeneration += 1;
  _bootstrapAttempted = false;
  _bootstrapInflight = null;
}

export function isBootstrapRateLimitError(error: unknown): boolean {
  return error instanceof Error && error.message === BOOTSTRAP_RATE_LIMIT_ERROR;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function retryDelayMs(response: Response, attempt: number): number {
  const retryAfter = Number(response.headers.get('retry-after') || '');
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(MAX_RATE_LIMIT_DELAY_MS, retryAfter * 1000);
  }
  return Math.min(MAX_RATE_LIMIT_DELAY_MS, MIN_RATE_LIMIT_DELAY_MS * (attempt + 1));
}

async function fetchAuthMeWithRateLimitRetry(force: boolean): Promise<Response> {
  for (let attempt = 0; attempt <= MAX_RATE_LIMIT_RETRIES; attempt += 1) {
    const response = await fetchAuthMeAuthedResponse({
      credentials: 'include',
      autoLogout: false,
      skipDedupe: force || attempt > 0,
    });
    if (response.status !== 429) return response;
    if (attempt === MAX_RATE_LIMIT_RETRIES) {
      throw new Error(BOOTSTRAP_RATE_LIMIT_ERROR);
    }
    await sleep(retryDelayMs(response, attempt));
  }
  throw new Error(BOOTSTRAP_RATE_LIMIT_ERROR);
}

interface RawUserLike {
  id?: number; user_id?: number; student_id?: number;
  full_name?: string; name?: string;
  username?: string; email?: string; role?: string; portal_role?: string;
  profile_completed?: boolean | null;
  is_developer?: boolean;
  can_manage_reception_all_branches?: boolean;
  simulation?: {
    active?: boolean;
    role?: string | null;
    target_employee_id?: number | null;
    target_student_id?: number | null;
    effects_policy?: string | null;
  } | null;
  user?: RawUserLike;
}

function buildUser(j: unknown): User | null {
  if(!j || typeof j !== 'object') return null;
  const obj = j as RawUserLike;
  const nested = obj.user as RawUserLike | undefined;
  const id = obj.id || obj.user_id || nested?.id || obj.student_id || -1;
  if(!id || id === -1) return null;
  const rawSimulation = obj.simulation || nested?.simulation || null;
  const simulation = rawSimulation
    ? {
        active: Boolean(rawSimulation.active),
        role: rawSimulation.role ?? null,
        target_employee_id: rawSimulation.target_employee_id ?? null,
        target_student_id: rawSimulation.target_student_id ?? null,
        effects_policy: rawSimulation.effects_policy ?? null,
      }
    : null;

  return {
    id,
    full_name: obj.full_name || obj.name || nested?.full_name || null,
    username: obj.username || nested?.username || null,
    email: obj.email || nested?.email || null,
    role: obj.role || nested?.role || obj.portal_role || null,
    profile_completed: obj.profile_completed ?? nested?.profile_completed ?? null,
    is_developer: Boolean(obj.is_developer ?? nested?.is_developer ?? false),
    can_manage_reception_all_branches: (obj.can_manage_reception_all_branches ?? nested?.can_manage_reception_all_branches) === true,
    simulation,
  };
}

export async function tryBootstrap(force = false): Promise<User | null> {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;
  if (!force && shouldSkipBootstrapForPath(path)) {
    return null;
  }
  const isEmployeeLoginPage = (
    path === '/employee/login'
    || path === '/employee/register'
    || path === '/employee/forgot-password'
    || path === '/employee/reset-password'
  );
  // Skip bootstrap only on login/register pages (unauthenticated pages)
  // For employee authenticated pages like /employee/attendance, we MUST check session
  if (isEmployeeLoginPage && !force) {
    _bootstrapAttempted = true;
    return null;
  }
  // Optimization: If user hits an unknown top-level path (e.g. /fefe) that triggered
  // SPA 404 fallback, avoid spamming all legacy role endpoints when there is
  // clearly no authenticated session. We detect this by checking that the path
  // does NOT start with any known app segment and sessionStorage has no auth hint.
  const KNOWN_PREFIXES = ['/', '/student', '/reception', '/hr', '/manager', '/instructor', '/languages', '/employee'];
  const looksUnknown = !KNOWN_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
  if(looksUnknown && !force){
    // Heuristic: if we've already attempted once OR there is no auth cookie hint, skip.
    // We cannot read HttpOnly cookies directly; rely on attempt flag.
    if(_bootstrapAttempted){
      return null;
    }
  }
  if(_bootstrapAttempted && !force) return null;
  if (_bootstrapInflight && !force) return _bootstrapInflight;

  const generation = _bootstrapGeneration;
  const markAttempted = () => {
    if (generation === _bootstrapGeneration) _bootstrapAttempted = true;
  };
  const run = async (): Promise<User | null> => {
    const response = await fetchAuthMeWithRateLimitRetry(force);
    if (response.status === 401) {
      markAttempted();
      return null;
    }
    if (!response.ok) {
      throw new Error(`${UNEXPECTED_STATUS_ERROR}:${response.status}`);
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new Error(INVALID_PAYLOAD_ERROR);
    }
    const authenticatedUser = buildUser(payload);
    if (!authenticatedUser) {
      throw new Error(INVALID_PAYLOAD_ERROR);
    }
    markAttempted();
    return authenticatedUser;
  };

  const tracked = run().finally(() => {
    if (_bootstrapInflight === tracked) _bootstrapInflight = null;
  });
  _bootstrapInflight = tracked;
  return tracked;
}


